import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import type { Router } from 'vue-router';

async function updateStatusBar() {
  await Promise.allSettled([
    SystemBars.setStyle({ style: SystemBarsStyle.Dark, bar: SystemBarType.StatusBar }),
    StatusBar.setOverlaysWebView({ overlay: true }),
    StatusBar.setStyle({ style: Style.Dark }),
  ]);
}

function closeVisibleOverlay() {
  const overlay = [...document.querySelectorAll<HTMLElement>('.el-overlay')].find(
    (element) => getComputedStyle(element).display !== 'none',
  );
  if (!overlay) return false;
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  return true;
}

export async function initializeNativeApp(router: Router) {
  if (!Capacitor.isNativePlatform()) return;

  const setKeyboardOpen = (open: boolean) => {
    document.documentElement.classList.toggle('native-keyboard-open', open);
  };

  const revealFocusedControl = () => {
    window.setTimeout(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);
  };

  await Promise.allSettled([
    updateStatusBar(),
    SplashScreen.hide(),
  ]);

  router.afterEach(() => {
    void updateStatusBar();
  });

  await Keyboard.addListener('keyboardWillShow', () => {
    setKeyboardOpen(true);
    revealFocusedControl();
  });

  await Keyboard.addListener('keyboardDidShow', () => {
    setKeyboardOpen(true);
    revealFocusedControl();
  });

  await Keyboard.addListener('keyboardDidHide', () => {
    setKeyboardOpen(false);
  });

  await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (closeVisibleOverlay()) return;
    if (canGoBack && window.history.length > 1) router.back();
    else void CapacitorApp.exitApp();
  });
}
